import React, { useState, useRef, useEffect } from "react";
import { initSoundEffects } from "@/services/SoundEffects";
import type { Station } from "@/lib/api-client";
import { EmbedHeader } from "./EmbedHeader";
import { EmbedNowPlaying } from "./EmbedNowPlaying";

interface EmbedDemoPlayerProps {
  station: Station;
  theme?: "light" | "dark";
}

export function EmbedDemoPlayer({ station, theme = "dark" }: EmbedDemoPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);

  // Initialize sound effects
  useEffect(() => {
    initSoundEffects();
  }, []);

  const handlePlay = async () => {
    if (!audioRef.current) return;

    try {
      setIsLoading(true);
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error("Failed to play audio:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };

  // Theme classes
  const bgColor = theme === "dark" ? "bg-[#081506]" : "bg-gray-50";
  const textColor = theme === "dark" ? "text-[#6adf47]" : "text-gray-900";
  const mutedTextColor = theme === "dark" ? "text-[#4fa935]" : "text-gray-600";
  const buttonBg = theme === "dark" ? "bg-[#6adf47]" : "bg-gray-900";
  const buttonText = theme === "dark" ? "text-[#081506]" : "text-white";

  const stationUrl = `https://macrowave.co/${station.broadcaster.username}/${station.slug}`;

  return (
    <div className={`h-full flex flex-col ${bgColor} ${textColor} font-array p-4 relative`}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src="/apollo11.mp3"
        loop
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onError={() => setAudioError(true)}
      />

      {/* Corner Link to Station */}
      <a
        href={stationUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed top-2 right-2 z-50 px-3 py-1.5 rounded-full text-xs font-bold transition-all hover:scale-105 ${
          theme === "dark"
            ? "bg-[#6adf47]/10 text-[#6adf47] border border-[#6adf47]/30 hover:bg-[#6adf47]/20"
            : "bg-gray-900/10 text-gray-900 border border-gray-900/30 hover:bg-gray-900/20"
        }`}
        style={{
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      >
        View on Macrowave ↗
      </a>

      {/* Header */}
      <EmbedHeader
        station={station}
        isLive={true}
        theme={theme}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full space-y-4">
          {/* Now Playing */}
          <EmbedNowPlaying
            nowPlaying={station.nowPlaying}
            isConnected={isPlaying}
            theme={theme}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-3 mt-4">
        <button
          onClick={isPlaying ? handlePause : handlePlay}
          disabled={isLoading || audioError}
          className={`w-full py-3 rounded-lg font-bold text-sm tracking-wide transition-all ${buttonBg} ${buttonText} ${
            isLoading || audioError ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.02] active:scale-[0.98]"
          }`}
        >
          {audioError ? "Audio Not Available" : isLoading ? "Loading..." : isPlaying ? "■ Stop" : "▶ Play"}
        </button>

        <div className={`text-xs text-center ${mutedTextColor}`}>
          {audioError
            ? "Audio file unavailable"
            : isPlaying
            ? "Now playing"
            : "Click to listen"}
        </div>
      </div>
    </div>
  );
}
